import { Injectable, computed, effect, inject, signal } from '@angular/core';

import { DocsFacade } from '../state/docs/docs.facade';
import { TicketsExecutionStore } from '../state/tickets/tickets-execution.store';
import { TicketsFacade } from '../state/tickets/tickets.facade';

import { ToastService } from './toast.service';
import { TicketExecutionEvent, TicketsBuildRun, TicketsExecService } from './tickets-exec.service';
import { buildExecutionRuntimeFromRun } from '../utils/tickets-run-runtime.util';
import { buildReposPayload, buildTicketsProjectContext, loadReposSettings } from '../utils/tickets-repos-context.util';
import type { RepoRole } from '../utils/repo-role.util';

@Injectable({ providedIn: 'root' })
export class TicketsAutomationService {
  private ticketsStore = inject(TicketsFacade);
  private execStore = inject(TicketsExecutionStore);
  private docs = inject(DocsFacade);
  private execSvc = inject(TicketsExecService);
  private toast = inject(ToastService);
  private lastProjectId: string | null = null;

  public currentRun = signal<TicketsBuildRun | null>(null);
  public startBusy = signal(false);
  private pollHandle: ReturnType<typeof setTimeout> | null = null;
  private followAbort: AbortController | null = null;

  public activeRun = computed(() => {
    const run = this.currentRun();
    if (!run) return null;
    return ['queued', 'planning', 'running'].includes(String(run.status ?? '')) ? run : null;
  });

  constructor() {
    effect(() => {
      const projectId = this.ticketsStore.activeProjectId();
      if (projectId === this.lastProjectId) return;
      this.stopPolling();
      this.currentRun.set(null);
      this.execStore.resetExecutionRuntime();
      this.lastProjectId = projectId;
    });
  }

  private applyRunToStore(run: TicketsBuildRun, modelFallback?: string) {
    this.currentRun.set(run);
    this.execStore.setExecutionRuntime(
      buildExecutionRuntimeFromRun(run, {
        modelFallback,
        currentModel: this.execStore.execution().model,
        liveFollowConnected: !!this.followAbort,
      })
    );
  }

  private mergeRunEvents(run: TicketsBuildRun, events: TicketExecutionEvent[]): TicketsBuildRun {
    const existing = Array.isArray(run.events) ? run.events.slice() : [];
    const merged = [...existing, ...events]
      .sort((a, b) => Number(a?.seq ?? 0) - Number(b?.seq ?? 0))
      .filter((event, index, all) => index === 0 || Number(event?.seq ?? 0) !== Number(all[index - 1]?.seq ?? -1))
      .slice(-150);
    return {
      ...run,
      latestEventSeq: merged.length ? Number(merged[merged.length - 1]?.seq ?? run.latestEventSeq ?? 0) : run.latestEventSeq,
      events: merged,
    };
  }

  private async followRunStream(runId: string, modelFallback?: string): Promise<void> {
    if (this.followAbort) {
      this.followAbort.abort();
      this.followAbort = null;
    }
    const current = this.currentRun();
    const afterSeq = typeof current?.latestEventSeq === 'number' ? current.latestEventSeq : undefined;
    const controller = new AbortController();
    this.followAbort = controller;
    try {
      const response = await this.execSvc.followExecutionRunStream(runId, afterSeq, controller.signal);
      if (!response.body) throw new Error('Run follow stream did not return a body');
      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        let idx = buffer.indexOf('\n\n');
        while (idx >= 0) {
          const chunk = buffer.slice(0, idx);
          buffer = buffer.slice(idx + 2);
          idx = buffer.indexOf('\n\n');
          const lines = chunk.split('\n');
          const eventName = lines.find((line) => line.startsWith('event:'))?.slice(6).trim() || 'message';
          const dataLine = lines.filter((line) => line.startsWith('data:')).map((line) => line.slice(5).trim()).join('\n');
          if (!dataLine) continue;
          let parsed: any = null;
          try {
            parsed = JSON.parse(dataLine);
          } catch {
            continue;
          }
          if (eventName === 'snapshot' || eventName === 'done') {
            this.applyRunToStore(parsed as TicketsBuildRun, modelFallback);
            if (eventName === 'done') return;
          } else if (eventName === 'events') {
            const base = parsed?.run as TicketsBuildRun;
            const events = Array.isArray(parsed?.events) ? parsed.events : [];
            this.applyRunToStore(this.mergeRunEvents(base, events), modelFallback);
          }
        }
      }
    } catch {
      // fall back to polling below
    } finally {
      if (this.followAbort === controller) {
        this.followAbort = null;
      }
    }
  }

  stopPolling() {
    if (this.pollHandle) {
      clearTimeout(this.pollHandle);
      this.pollHandle = null;
    }
    if (this.followAbort) {
      this.followAbort.abort();
      this.followAbort = null;
    }
  }

  startPolling(runId: string, modelFallback?: string) {
    this.stopPolling();
    const tick = async () => {
      try {
        const run = await this.execSvc.getExecutionRun(runId);
        this.applyRunToStore(run, modelFallback);
        const projectId = this.ticketsStore.activeProjectId();
        if (projectId && String(run.projectId ?? '') === String(projectId)) {
          await this.ticketsStore.refreshBoard(projectId);
        }
        if (run.status === 'queued' || run.status === 'planning' || run.status === 'running') {
          void this.followRunStream(run.id, modelFallback);
          this.pollHandle = setTimeout(() => void tick(), 3000);
          return;
        }
        this.stopPolling();
      } catch (e: any) {
        this.stopPolling();
        this.toast.error(e?.message ?? 'Failed to poll execution status', 'Tickets');
      }
    };
    void tick();
  }

  async hydratePersistedRun(modelFallback?: string): Promise<TicketsBuildRun | null> {
    const current = this.currentRun();
    if (current) return current;
    const projectId = this.ticketsStore.activeProjectId();
    const runId = String(this.execStore.getPersistedExecutionRunId(projectId) ?? '').trim();
    if (!projectId || !runId) return null;
    try {
      const run = await this.execSvc.getExecutionRun(runId);
      if (String(run.projectId ?? '').trim() !== String(projectId).trim()) {
        this.execStore.clearPersistedExecutionRunId(projectId);
        return null;
      }
      this.applyRunToStore(run, modelFallback);
      if (run.status === 'queued' || run.status === 'planning' || run.status === 'running') {
        this.startPolling(run.id, modelFallback);
      }
      return run;
    } catch {
      this.execStore.clearPersistedExecutionRunId(projectId);
      return null;
    }
  }

  async startBuildProject(model: string): Promise<TicketsBuildRun | null> {
    const projectId = this.ticketsStore.activeProjectId();
    if (!projectId) {
      this.toast.warning('Select a project first', 'Tickets');
      return null;
    }
    const selectedModel = String(model ?? '').trim() || String(this.ticketsStore.executionModelId() ?? '').trim();
    this.startBusy.set(true);
    try {
      const reposSettings = await loadReposSettings(this.docs, projectId);
      const repos = buildReposPayload(reposSettings);
      const installationIdRaw = reposSettings?.github?.installationId ?? null;
      const installationId = typeof installationIdRaw === 'number' ? installationIdRaw : Number(String(installationIdRaw ?? '').trim());
      const run = await this.execSvc.startBuildProject({
        projectId,
        installationId: Number.isFinite(installationId) && installationId > 0 ? installationId : undefined,
        repos,
        model: selectedModel || undefined,
        projectContext: buildTicketsProjectContext(this.ticketsStore, reposSettings),
      });
      this.applyRunToStore(run, selectedModel);
      this.startPolling(run.id, selectedModel);
      this.toast.success('Project build started', 'Tickets');
      return run;
    } catch (e: any) {
      this.toast.error(e?.message ?? 'Failed to start project build', 'Tickets');
      return null;
    } finally {
      this.startBusy.set(false);
    }
  }

  async startTicketExecutionRun(input: {
    projectId: string;
    installationId: number;
    repos: Array<{ owner: string; repo: string; role?: RepoRole }>;
    target?: {
      owner: string;
      repo: string;
      role?: RepoRole;
      confidence?: number;
      reason?: string;
      source?: 'auto' | 'user-confirmed' | 'explicit';
    };
    model?: string;
    ticket: {
      id: string;
      title: string;
      description?: string;
      repoRole?: RepoRole;
      acceptanceCriteria?: string[];
      risk?: 'low' | 'med' | 'high';
    };
    projectContext?: { definition?: any; schema?: any; diagram?: any; repos?: any };
  }): Promise<TicketsBuildRun | null> {
    try {
      const run = await this.execSvc.startExecuteTicketAgentRun({
        projectId: input.projectId,
        installationId: input.installationId,
        repos: input.repos,
        target: input.target,
        baseBranch: 'dev',
        model: input.model || undefined,
        ticket: input.ticket,
        projectContext: input.projectContext,
      });
      this.applyRunToStore(run, input.model);
      this.startPolling(run.id, input.model);
      return run;
    } catch (e: any) {
      this.toast.error(e?.message ?? 'Failed to start ticket execution', 'Tickets');
      return null;
    }
  }

  async cancelRun(runId: string, modelFallback?: string): Promise<TicketsBuildRun | null> {
    try {
      const run = await this.execSvc.cancelExecutionRun(runId);
      this.applyRunToStore(run, modelFallback);
      this.stopPolling();
      return run;
    } catch (e: any) {
      this.toast.error(e?.message ?? 'Failed to cancel execution', 'Tickets');
      return null;
    }
  }

  private syncRunPolling(run: TicketsBuildRun, modelFallback?: string): void {
    if (run.status === 'queued' || run.status === 'planning' || run.status === 'running') {
      this.startPolling(run.id, modelFallback);
      return;
    }
    this.stopPolling();
  }

  private applyRunOutcome(run: TicketsBuildRun, modelFallback?: string): TicketsBuildRun {
    this.applyRunToStore(run, modelFallback);
    this.syncRunPolling(run, modelFallback);
    return run;
  }

  async resolveRunDecision(runId: string, action: 'continue' | 'skip' | 'cancel', modelFallback?: string): Promise<TicketsBuildRun | null> {
    try {
      const run = await this.execSvc.resolveExecutionRunDecision(runId, action);
      return this.applyRunOutcome(run, modelFallback);
    } catch (e: any) {
      this.toast.error(e?.message ?? 'Failed to update execution decision', 'Tickets');
      return null;
    }
  }

  async resumeRun(runId: string, modelFallback?: string, ticketId?: string): Promise<TicketsBuildRun | null> {
    try {
      const run = await this.execSvc.resumeExecutionRun(runId, ticketId);
      return this.applyRunOutcome(run, modelFallback);
    } catch (e: any) {
      this.toast.error(e?.message ?? 'Failed to resume execution run', 'Tickets');
      return null;
    }
  }
}
